import { CtaBlock } from '@design-system/cta-block';
import { CtaBlockProps } from '@design-system/cta-block/dist/types/models';
import { Modal } from '@design-system/modal';
import { Typography } from '@design-system/typography';
import {
  getLocalStorageData,
  setLocalStorageData,
} from '@phx-husky/local-storage';
import React, { FC, useEffect, useState } from 'react';

import { useTranslations } from '~/context/TranslationContext/TranslationsContext';

const PROFESSION_CONFIRMATION_POPUP_DISPLAYED =
  'profession-confirmation-popup-displayed';

const DOCUMENT_LOCK_CLASSNAME = 'cds-scroll-locked';

const ProfessionConfirmationDialog: FC = () => {
  const [popupVisible, setPopupVisible] = useState(false);

  const t = useTranslations();

  const modalHeaderText =
    t?.professionConfirmationDialog?.header ??
    'This site is for healthcare professionals only';

  const modalBodyText =
    t?.professionConfirmationDialog?.bodyText ??
    'Are you a healthcare professional?';

  const modalDeclineBtnText =
    t?.professionConfirmationDialog?.declineButton ?? 'No';

  const modalAcceptBtnText =
    t?.professionConfirmationDialog?.acceptButton ?? 'Yes';

  useEffect(() => {
    const localStorageValue = getLocalStorageData(
      PROFESSION_CONFIRMATION_POPUP_DISPLAYED
    );
    if (!localStorageValue) {
      setPopupVisible(true);
    }

    return undefined;
  }, []);

  useEffect(() => {
    if (popupVisible) {
      document.documentElement.classList.add(DOCUMENT_LOCK_CLASSNAME);
    } else {
      document.documentElement.classList.remove(DOCUMENT_LOCK_CLASSNAME);
    }
  }, [popupVisible]);

  useEffect(() => {
    if (!popupVisible) return undefined;

    const FOCUSABLE_SELECTORS =
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

    const getModalFocusable = (): HTMLElement[] => {
      const modal = document.querySelector('.profession-confirmation-dialog');

      return modal
        ? Array.from(modal.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTORS))
        : [];
    };

    const timerId = setTimeout(() => {
      const focusable = getModalFocusable();
      if (focusable.length > 0) focusable[0].focus();
    }, 0);

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const focusable = getModalFocusable();
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      clearTimeout(timerId);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [popupVisible]);

  const onClose = (e?: React.MouseEvent) => {
    e?.preventDefault();
    setPopupVisible(false);
    setLocalStorageData(PROFESSION_CONFIRMATION_POPUP_DISPLAYED, true);
  };

  const actionButtonData: CtaBlockProps['data'] = [
    {
      elementType: 'link',
      variant: 'primary',
      size: 'large',
      children: modalDeclineBtnText,
      className: 'decline-button',
      href: 'https://reckitt.com/',
    },
    {
      elementType: 'link',
      variant: 'primary',
      size: 'large',
      children: modalAcceptBtnText,
      className: 'accept-button',
      href: '#',
      onClick: onClose,
    },
  ];

  return (
    <Modal
      isShowing={popupVisible}
      closeModal={() => false}
      className="profession-confirmation-dialog"
      showCloseButton={false}
      topElements={
        <Typography
          align="center"
          tag="h3"
          weight="bold"
          variant="headline"
          size="m"
          content={modalHeaderText}
        />
      }
    >
      <Typography
        align="center"
        tag="div"
        weight="regular"
        variant="body"
        size="m"
        content={modalBodyText}
      />
      <div className="cta-wrapper">
        <CtaBlock align="center" data={actionButtonData} />
      </div>
    </Modal>
  );
};

export default ProfessionConfirmationDialog;
